import clsx from 'clsx'
import Link from 'next/link'
import { useEffect, useState, type FC } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'

import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderItem,
  TableRow,
  TableRowItem,
  TableShimmer,
} from 'app/KinerjaShared/components/Table'
import TableRowStates from 'app/KinerjaShared/components/TableRowStates'
import { formatPeriod } from 'app/KinerjaShared/utils/period'
import { periodConfig } from 'app/KinerjaRiwayat/constants'
import {
  PENGELOLAAN_KINERJA_RIWAYAT_GURU_PATH,
  PENGELOLAAN_KINERJA_RIWAYAT_KS_PATH,
} from 'app/KinerjaShared/constants/path'
import { RoleLabelShort } from 'app/KinerjaKSPage/utils/profile'
import { useApiGetRiwayatByYear } from 'api/kinerja/kepala-sekolah/riwayat'
import useSKPRiwayatTrackEvent from 'app/KinerjaRiwayat/hooks/useSKPRiwayatTrackEvent'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import styles from 'app/KinerjaRiwayat/pages/styles.module.css'
import ButtonSelengkapnya from 'app/KinerjaRiwayat/components/ButtonSelengkapnya'
import useToggleSdkContext from 'utils/useToogleSdk'
import DialogBlockPeriodNonAsn from 'app/KinerjaShared/components/DialogBlockPeriodNonAsn'

import type { TFilterItem } from 'app/KinerjaRiwayat/pages/Riwayat'
import type { KinerjaRoleType } from 'types/kinerja'
import type { TRiwayatItem } from 'types/kinerja-riwayat-ks'

type RiwayatTahunanProps = {
  showVersion2?: boolean
  activeFilter: TFilterItem[]
  activeYear: string
  rowHeaderClasses: string
  rowItemClasses: string
  trackerFilter: Record<TFilterItem, string>
  onLoading?: (isLoading: boolean) => void
  isLoading?: boolean
}

const getUrl = (item: TRiwayatItem) => {
  if (item?.role === 'GURU') {
    if (!!item?.year && !!item?.period)
      return `${PENGELOLAAN_KINERJA_RIWAYAT_GURU_PATH}?year=${item.year}&period=${item.period}`
    return null
  }

  return {
    pathname: PENGELOLAAN_KINERJA_RIWAYAT_KS_PATH,
    query: { riwayatId: item?.id },
  }
}
const RiwayatPeriode: FC<RiwayatTahunanProps> = ({
  showVersion2 = false,
  activeFilter,
  activeYear,
  rowHeaderClasses,
  rowItemClasses,
  trackerFilter,
  onLoading,
  isLoading: isParentLoading = false,
}) => {
  const { getPeriod } = usePeriodContext()
  const { trackEvent } = useSKPRiwayatTrackEvent()
  const toggle = useToggleSdkContext()

  const isBlockNonAsnEnabled = toggle?.['kinerja-block-non-asn-period'] === true

  const [isBlockDialogOpen, setBlockDialogOpen] = useState(false)

  const {
    isLoading: isRiwayatYearLoading,
    isSuccess,
    isError,
    data: riwayatData,
    refetch: refetchRiwayat,
  } = useApiGetRiwayatByYear(
    {
      year: activeYear,
      roles: activeFilter?.includes('ALL')
        ? null
        : (activeFilter as Array<KinerjaRoleType>),
    },
    { enabled: !!activeFilter && !!activeYear }
  )

  const hasData = riwayatData?.data?.length > 0

  useEffect(() => {
    onLoading?.(isRiwayatYearLoading)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRiwayatYearLoading])

  const isLoading = isRiwayatYearLoading || isParentLoading

  return (
    <section className="flex flex-col gap-6">
      {showVersion2 && <Text variant="heading-lg">Rincian Per Periode</Text>}

      <Table
        data-testid="table-riwayat-periode"
        className={clsx('!mt-0', styles.tableRiwayat)}
      >
        <TableHeader>
          <TableHeaderItem className={clsx(rowHeaderClasses, 'w-4/12')}>
            Periode
          </TableHeaderItem>
          <TableHeaderItem className={clsx(rowHeaderClasses, 'pl-0 w-4/12')}>
            Satuan Pendidikan
          </TableHeaderItem>
          <TableHeaderItem className={clsx(rowHeaderClasses, 'pl-0 w-2/12')}>
            Jabatan
          </TableHeaderItem>
          <TableHeaderItem className={clsx(rowHeaderClasses, 'pl-0 w-2/12')} />
        </TableHeader>

        <TableBody>
          {/* If loading */}
          {isLoading && <TableShimmer columns={4} />}

          {(isError || (isSuccess && !hasData)) && (
            <TableRowStates
              colSpan={4}
              type={isError ? 'ERROR' : 'EMPTY'}
              onClick={refetchRiwayat}
            >
              <>
                Pastikan terdapat Pengelolaan Kinerja Anda yang sudah
                <br />
                selesai di tahun yang dipilih.
              </>
            </TableRowStates>
          )}

          {isSuccess && !isLoading && (
            <>
              {riwayatData?.data?.map((item, index) => {
                const periodData = getPeriod({
                  periodNo: item?.period,
                  periodYear: item?.year,
                })

                const periodLabel = !!periodData
                  ? formatPeriod(
                      periodData?.startDate,
                      periodData?.endDate,
                      periodConfig
                    )
                  : item?.period

                const url = getUrl(item)
                const isBlocked = isBlockNonAsnEnabled && !item.accessible

                return (
                  <TableRow
                    key={`table-row-${index}`}
                    data-testid={`row-${index + 1}`}
                  >
                    {/* Periode */}
                    <TableRowItem className={rowItemClasses}>
                      {periodLabel}
                    </TableRowItem>

                    {/* Satuan Pendidikan */}
                    <TableRowItem className={clsx(rowItemClasses, 'pl-0')}>
                      {item?.satuanPendidikan}
                    </TableRowItem>

                    {/* Jabatan */}
                    <TableRowItem className={clsx(rowItemClasses, 'pl-0')}>
                      {RoleLabelShort[item?.role?.toUpperCase()]}
                    </TableRowItem>

                    {/* Buttons */}
                    <TableRowItem className={clsx(rowItemClasses, 'pl-0')}>
                      <div className="flex flex-col items-end gap-2">
                        {isBlocked ? (
                          <Button
                            size="sm"
                            color="white"
                            className="whitespace-nowrap space-x-1"
                            onClick={() => setBlockDialogOpen(true)}
                          >
                            <span>Tidak dapat diakses</span>
                            {/*
                             * Note: we need to explicitly set font-size and line-height as workaround of styles collision
                             * due to multiple version of @wartek-id/icon being used in this project.
                             */}
                            <Icon fontSize="sm" className="!leading-5 !text-xl">
                              help
                            </Icon>
                          </Button>
                        ) : (
                          /* If URL is undefined, due to missing period no & period year, hide the button */
                          !!url && (
                            <>
                              <Link href={url} passHref>
                                <ButtonSelengkapnya
                                  onClick={() => {
                                    trackEvent({
                                      activity_type:
                                        'click_selengkapnya_riwayat_kinerja',
                                      screen_name: 'beranda_riwayat_kinerja',
                                      event_trigger: 'beranda_riwayat_kinerja',
                                      attributes: {
                                        filter: trackerFilter[activeFilter[0]],
                                      },
                                    })
                                  }}
                                />
                              </Link>
                            </>
                          )
                        )}
                      </div>
                    </TableRowItem>
                  </TableRow>
                )
              })}
            </>
          )}
        </TableBody>
      </Table>

      <DialogBlockPeriodNonAsn
        isOpen={isBlockDialogOpen}
        onClose={() => setBlockDialogOpen(false)}
      />
    </section>
  )
}
export default RiwayatPeriode
